import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';

import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useViewingTagSelection } from '@/hooks/useViewingTagSelection';
import { useGetAccountsQuery } from '@/store/accountsApi';

interface UseInScopeAccountIdsArgs {
  endDate?: string;
  /**
   * Skip the viewing-tag account id filter. Live balances are still loaded
   * so a current-range comparison can replace a missing snapshot.
   */
  skipScope?: boolean;
  excludedAccounts?: number[];
  excludedAccountTypes?: number[];
  excludedCategories?: number[];
  excludedInstitutions?: number[];
}

interface UseInScopeAccountIdsResult {
  accountIds: Set<number> | undefined;
  /**
   * Accounts-page balance keyed by account id. Undefined until that list
   * resolves. Filled even when no Account Group is selected.
   */
  balancesByAccountId: ReadonlyMap<number, number> | undefined;
  isLoading: boolean;
}

/**
 * Account ids for the header Account Group, plus the Accounts-page balance
 * of each account returned for `endDate`.
 *
 * `accountIds` is `undefined` when no group is selected, the id filter is
 * skipped, or the accounts query has not resolved. `balancesByAccountId`
 * is filled whenever that query resolves.
 */
export function useInScopeAccountIds({
  endDate,
  skipScope = false,
  excludedAccounts,
  excludedAccountTypes,
  excludedCategories,
  excludedInstitutions,
}: UseInScopeAccountIdsArgs): UseInScopeAccountIdsResult {
  const authIsLoaded = useAuthIsLoaded();
  const viewingTag = useViewingTagSelection();
  const queryEnabled = Boolean(endDate);
  const scopeEnabled = queryEnabled && !skipScope && viewingTag !== null;

  const {
    currentData,
    isSuccess,
    isError,
    isLoading,
    isFetching,
    isUninitialized,
  } = useGetAccountsQuery(
    queryEnabled && authIsLoaded && endDate
      ? {
          showClosed: false,
          asOfDate: endDate,
          ...(excludedAccounts && excludedAccounts.length > 0
            ? { ExcludedAccounts: excludedAccounts }
            : {}),
          ...(excludedAccountTypes && excludedAccountTypes.length > 0
            ? { ExcludedAccountTypes: excludedAccountTypes }
            : {}),
          ...(excludedCategories && excludedCategories.length > 0
            ? { ExcludedCategories: excludedCategories }
            : {}),
          ...(excludedInstitutions && excludedInstitutions.length > 0
            ? { ExcludedInstitutions: excludedInstitutions }
            : {}),
        }
      : skipToken,
  );

  const { accountIds, balancesByAccountId } = useMemo(() => {
    // `data` can still be the previous date while this one is in flight.
    // `currentData` is only the result for the date being requested.
    if (!queryEnabled || !currentData) {
      return {
        accountIds: undefined,
        balancesByAccountId: undefined,
      };
    }

    const records = currentData.Records;
    if (!Array.isArray(records)) {
      return {
        accountIds: scopeEnabled ? new Set<number>() : undefined,
        balancesByAccountId: new Map<number, number>(),
      };
    }

    const ids = new Set<number>();
    const balances = new Map<number, number>();
    for (const account of records) {
      ids.add(account.AccountId);
      if (
        typeof account.Balance === 'number' &&
        Number.isFinite(account.Balance)
      ) {
        balances.set(account.AccountId, account.Balance);
      }
    }

    return {
      accountIds: scopeEnabled ? ids : undefined,
      balancesByAccountId: balances,
    };
  }, [currentData, queryEnabled, scopeEnabled]);

  return {
    accountIds,
    balancesByAccountId,
    isLoading:
      queryEnabled &&
      !currentData &&
      !isError &&
      (isUninitialized || isLoading || isFetching || !isSuccess),
  };
}
